/**
 * Share order modal — auto-generates link on open, inline copy feedback.
 */
import React, { useEffect, useState } from 'react';
import { createShareLink } from '../services/api';
import './ShareModal.css';

function ShareModal({ order, onClose, instance }) {
  const [generatedLink, setGeneratedLink] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const generate = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await createShareLink(order.order_id, null, 24, instance);
        if (!cancelled) {
          // Backend always returns the prod host. Keep the token, use this site.
          const token = String(data.shareable_link).split('/shared/').pop();
          setGeneratedLink(`${window.location.origin}/shared/${token}`);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to generate shareable link. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    generate();
    return () => { cancelled = true; };
  }, [order.order_id, instance]);

  /** Copy link to clipboard and show brief success state. */
  const handleCopyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy. Select the link and copy manually.');
    }
  };

  /** Close when backdrop is clicked. */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="share-overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
      >
        <h2 id="share-modal-title" className="share-modal__title">
          Share Order #{order.order_id}
        </h2>

        {loading && (
          <div className="share-modal__loading">
            <span className="share-modal__spinner" aria-hidden="true" />
            Generating shareable link…
          </div>
        )}

        {!loading && error && !generatedLink && (
          <>
            <p className="share-modal__error">{error}</p>
            <div className="share-modal__actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}

        {!loading && generatedLink && (
          <>
            <p className="share-modal__subtitle">
              Anyone with this link can track the order in real time.
            </p>
            {error && <p className="share-modal__error">{error}</p>}
            <div className="share-link-box">{generatedLink}</div>
            <div className="share-modal__actions">
              <button
                type="button"
                className={`btn-primary${copied ? ' copy-btn--copied btn-success' : ''}`}
                onClick={handleCopyLink}
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <button type="button" className="btn-secondary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ShareModal;
